/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import { type CrossAppItemResponse } from '@atlassian/navigation-bff-data-provider/universal-create';
import { ATI_PRESETS, type UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { CreationContext } from '../../../types/crossAppCreation';
import { getCreateContentLoader } from '../../integrations/forms/formLoader';

import { CrossAppItemBase } from './CrossAppItemBase';
import { CrossAppLinkItem } from './primitives/CrossAppLinkItem';
import { LoomVideoItem } from './primitives/LoomVideoItem';

export interface CrossAppItemProps {
	/**
	 * Additional context for the component
	 */
	context: CreationContext;

	/**
	 * Optional analytics injection callbacks for click tracking
	 */
	injectClickAnalytics?: {
		onClick?: (isRightClick: boolean) => void;
	};

	/**
	 * The source product context
	 */
	product: UniversalCreateSourceProduct;

	/**
	 * The response item from Universal Create
	 */
	responseItem: CrossAppItemResponse;
}

function renderPlatformCrossAppItem({
	responseItem,
	context,
	product,
	injectClickAnalytics,
}: {
	context: CreationContext;
	injectClickAnalytics?: { onClick?: (isRightClick: boolean) => void };
	product: UniversalCreateSourceProduct;
	responseItem: CrossAppItemResponse;
}) {
	switch (responseItem.ati) {
		case ATI_PRESETS.LOOM:
			return (
				<LoomVideoItem
					responseItem={responseItem}
					context={context}
					product={product}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		case ATI_PRESETS.GOAL:
		case ATI_PRESETS.PROJECT:
		case ATI_PRESETS.TEAM: {
			if (!fg('navx_uc_platformisation_forms_enabled')) {
				return null; // should not show this item if the feature flag is off
			}

			const loadCreateContent = getCreateContentLoader(responseItem.ati);

			const customisedItemConfig = {
				loadCreateContent,
				onClickCloseMenu: !loadCreateContent,
			};

			return (
				<CrossAppItemBase
					responseItem={responseItem}
					context={context}
					customisedItemConfig={customisedItemConfig}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		}
		default:
			return undefined;
	}
}

function renderCrossAppItem({
	responseItem,
	context,
	product,
	injectClickAnalytics,
}: {
	context: CreationContext;
	injectClickAnalytics?: { onClick?: (isRightClick: boolean) => void };
	product: UniversalCreateSourceProduct;
	responseItem: CrossAppItemResponse;
}) {
	switch (responseItem.type) {
		case 'platform':
			return renderPlatformCrossAppItem({
				responseItem,
				context,
				product,
				injectClickAnalytics,
			});
		case 'link':
			return (
				<CrossAppLinkItem
					responseItem={responseItem}
					context={context}
					product={product}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		default:
			break;
	}

	return undefined;
}

function CrossAppItemInner({
	responseItem,
	context,
	product,
	injectClickAnalytics,
}: CrossAppItemProps) {
	const renderedItem = renderCrossAppItem({
		responseItem,
		context,
		product,
		injectClickAnalytics,
	});

	if (renderedItem !== undefined) {
		return renderedItem;
	}

	// Return general cross-app item with no customisedItemConfig
	return (
		<CrossAppItemBase
			responseItem={responseItem}
			context={context}
			injectClickAnalytics={injectClickAnalytics}
		/>
	);
}

/**
 * A component that renders a cross-app creation item.
 * Wrapped by the ErrorBoundary component to handle errors so that it won't break the whole menu.
 *
 * @param props - @see CrossAppItemProps
 * @returns - JSX.Element @see CrossAppItemInner
 */
export function CrossAppItem(props: CrossAppItemProps) {
	return (
		<ErrorBoundary fallback={null}>
			<CrossAppItemInner {...props} />
		</ErrorBoundary>
	);
}
